import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Linkedin, Copy, CheckCircle, Loader2, Zap, TrendingUp } from 'lucide-react';
import { logger } from '../utils/logger';
import { API } from '../utils/apiBase';
import { useFeatureGate } from '../utils/useFeatureGate';


const ScoreBadge = ({ score }) => {
  const color = score >= 75 ? '#10b981' : score >= 50 ? '#eab308' : '#e11d48';
  return (
    <span className="text-3xl font-black" style={{ color }} data-testid="profile-score">{score}</span>
  );
};

export default function LinkedInOptimizer() {
  const navigate = useNavigate();
  const apiRef = useRef(API);
  const { checkGate, gateModal } = useFeatureGate();
  const [headline, setHeadline] = useState('');
  const [about, setAbout] = useState('');
  const [targetRole, setTargetRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [error, setError] = useState('');

  const handleOptimize = useCallback(async () => {
    if (!targetRole.trim()) return;
    if (await checkGate('linkedin_optimize')) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await axios.post(`${apiRef.current}/linkedin/optimize`, {
        current_headline: headline || undefined,
        current_about: about || undefined,
        target_role: targetRole,
      }, { withCredentials: true });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Optimization failed.');
      logger.error('LinkedIn optimize failed:', err);
    } finally {
      setLoading(false);
    }
  }, [headline, about, targetRole, checkGate]);

  const copyText = useCallback((text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  }, []);

  return (
    <div className="min-h-screen bg-[#f4f5f7] pt-20" data-testid="linkedin-optimizer-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate('/dashboard')} className="inline-flex items-center space-x-2 text-[#4b5563] hover:text-[#002fa7] mb-4" data-testid="back-button">
          <ArrowLeft size={20} /><span>Back to Dashboard</span>
        </button>
        <h1 className="font-satoshi text-3xl font-black tracking-tight text-[#0f1115] mb-1">LinkedIn Optimizer</h1>
        <p className="text-[#4b5563] mb-8">AI-powered headline and About section optimization</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input */}
          <div className="space-y-4">
            <div className="dashboard-card p-6">
              <label className="block text-sm font-semibold text-[#0f1115] mb-2">
                <Linkedin size={16} className="inline mr-1 text-[#0077b5]" /> Target Role *
              </label>
              <input
                type="text"
                value={targetRole}
                onChange={(e) => setTargetRole(e.target.value)}
                placeholder="e.g., Senior Software Engineer"
                className="w-full px-4 py-3 border border-[#e5e7eb] text-sm focus:border-[#002fa7] transition-colors"
                data-testid="target-role-input"
              />
            </div>

            <div className="dashboard-card p-6">
              <label className="block text-sm font-semibold text-[#0f1115] mb-2">Current Headline (optional)</label>
              <input
                type="text"
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                placeholder="e.g., Software Developer at TechCorp"
                className="w-full px-4 py-3 border border-[#e5e7eb] text-sm focus:border-[#002fa7] transition-colors"
                data-testid="headline-input"
              />
            </div>

            <div className="dashboard-card p-6">
              <label className="block text-sm font-semibold text-[#0f1115] mb-2">Current About Section (optional)</label>
              <textarea
                value={about}
                onChange={(e) => setAbout(e.target.value)}
                rows={8}
                placeholder="Paste your current LinkedIn About section..."
                className="w-full px-4 py-3 border border-[#e5e7eb] text-sm focus:border-[#002fa7] transition-colors resize-none"
                data-testid="about-input"
              />
            </div>

            <button
              onClick={handleOptimize}
              disabled={loading || !targetRole.trim()}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="optimize-btn"
            >
              {loading ? <><Loader2 size={18} className="animate-spin" /> Optimizing...</> : 'Optimize My Profile'}
            </button>
            {error && <p className="text-sm text-[#e11d48]" data-testid="error-msg">{error}</p>}
          </div>

          {/* Results */}
          <div className="space-y-4">
            {result ? (
              <>
                <div className="dashboard-card p-6 text-center" data-testid="score-card">
                  <p className="text-xs uppercase tracking-wider text-[#6b7280] mb-1">Profile Score</p>
                  <ScoreBadge score={result.profile_score} />
                  <p className="text-xs text-[#6b7280] mt-1">/ 100</p>
                </div>

                <div className="dashboard-card p-6" data-testid="optimized-headline">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-bold text-[#0f1115]">Optimized Headline</h3>
                    <button onClick={() => copyText(result.optimized_headline, 'headline')} className="text-xs text-[#002fa7] flex items-center gap-1">
                      {copiedField === 'headline' ? <><CheckCircle size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                    </button>
                  </div>
                  <p className="text-[#0f1115] font-medium text-sm">{result.optimized_headline}</p>
                </div>

                <div className="dashboard-card p-6" data-testid="optimized-about">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-bold text-[#0f1115]">Optimized About</h3>
                    <button onClick={() => copyText(result.optimized_about, 'about')} className="text-xs text-[#002fa7] flex items-center gap-1">
                      {copiedField === 'about' ? <><CheckCircle size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                    </button>
                  </div>
                  <p className="text-[#4b5563] text-sm whitespace-pre-wrap leading-relaxed">{result.optimized_about}</p>
                </div>

                {result.keyword_suggestions?.length > 0 && (
                  <div className="dashboard-card p-6" data-testid="keywords-card">
                    <h3 className="text-sm font-bold text-[#0f1115] mb-3 flex items-center gap-1"><TrendingUp size={14} className="text-[#002fa7]" /> Keywords to Add</h3>
                    <div className="flex flex-wrap gap-2">
                      {result.keyword_suggestions.map((kw) => (
                        <span key={kw} className="px-3 py-1 bg-[#002fa7]/10 text-[#002fa7] text-xs font-semibold">{kw}</span>
                      ))}
                    </div>
                  </div>
                )}

                {result.quick_wins?.length > 0 && (
                  <div className="dashboard-card p-6" data-testid="quick-wins-card">
                    <h3 className="text-sm font-bold text-[#0f1115] mb-3 flex items-center gap-1"><Zap size={14} className="text-[#eab308]" /> Quick Wins</h3>
                    <ul className="space-y-2">
                      {result.quick_wins.map((win) => (
                        <li key={win} className="text-sm text-[#4b5563] flex items-start gap-2">
                          <span className="mt-1.5 w-1.5 h-1.5 bg-[#eab308] rounded-full flex-shrink-0" />{win}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <div className="dashboard-card p-12 text-center text-[#6b7280]">
                <Linkedin size={48} className="mx-auto mb-4 text-[#d1d5db]" />
                <p className="font-semibold">Your optimized profile will appear here</p>
                <p className="text-sm mt-1">Enter your target role to get started</p>
              </div>
            )}
          </div>
        </div>
      </div>
      {gateModal}
    </div>
  );
}
